import React, { Component } from 'react';
import { themr } from 'react-css-themr';
import { Table, Column, Cell } from 'fixed-data-table-2';
import FontIcon from 'react-toolbox/lib/font_icon';
import { AutoSizer } from 'react-virtualized';
import classnames from 'classnames';
import _ from 'lodash';
import { SpanLoader } from 'common/components/core/wrappers/Span';
import theme from './SortedTable.scss';

class SortCell extends React.Component {
    onClick = () => {
        this.props.onSort(this.props.columnKey);
    };
    render() {
        const { columnKey, onSort, ...others } = this.props; // eslint-disable-line
        return <Cell {...others} onClick={this.onClick} />;
    }
}
const LOADING_ROW_COUNT = 20;
class SortedTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sortedBy: this.props.sortedBy,
            reverse: false,
            sortedSource: this.sort(
                this.props.source,
                this.props.sortedBy,
                false
            )
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            sortedSource: this.sort(
                nextProps.source,
                this.state.sortedBy,
                this.state.reverse
            )
        });
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !(
            _.isEqual(this.props, nextProps) && _.isEqual(this.state, nextState)
        );
    }

    onSort = key => {
        var reverse;
        key === this.state.sortedBy
            ? (reverse = !this.state.reverse)
            : (reverse = false);
        this.setState(
            {
                sortedBy: key,
                reverse,
                sortedSource: this.sort(this.props.source, key, reverse)
            },
            () => {
                if (this.props.onSort) {
                    this.props.onSort(key, reverse);
                }
            }
        );
    };

    sort = (data, key, reverse) => {
        if (!data) {
            return;
        }
        const { customSorter } = this.props;
        if (customSorter) {
            return customSorter(data, key, reverse);
        }
        var sortedData;
        var comparator = (valueA, valueB) => {
            if (!valueA) return -1;
            else if (!valueB) return 1;
            else if (valueA < valueB) return -1;
            else if (valueA > valueB) return 1;
            else return 0;
        };

        const sortKey = _.get(this.props.model, `${key}.sortKey`) || key;
        if (sortKey) {
            sortedData = data.sortBy(row => row.getIn(sortKey), comparator);
            if (reverse) {
                sortedData = sortedData.reverse();
            }
        } else {
            sortedData = data;
        }
        return sortedData;
    };

    getDataByRowAndColumn = props => {
        const { rowIndex, height, width, columnKey } = props;
        const { sortedSource, sortedBy } = this.state;
        const { theme } = this.props;
        var isSorted = sortedBy === columnKey;
        let isLoading;
        let content;
        if (!sortedSource) {
            isLoading = true;
        } else {
            var rowData = sortedSource.get(rowIndex);
            isLoading = false;
            var row = this.props.rowMapper(rowData, this.props.rowMapperParams);
            content = row[columnKey];
        }
        return (
            <Cell
                height={height}
                width={width}
                className={classnames({
                    [theme[`${columnKey}_cell`]]: content,
                    [theme.loadingCell]: isLoading,
                    [theme.sortedCell]: isSorted
                })}
            >
                <SpanLoader loading={isLoading} className={theme.contentSpan}>
                    {content}
                </SpanLoader>
            </Cell>
        );
    };

    getHeader = props => {
        const { model } = this.props;
        const { columnKey } = props;
        const { title } = model[columnKey];
        var sortIcon;
        var isSorted = this.state.sortedBy === columnKey;
        const { theme } = this.props;
        if (isSorted) {
            if (this.state.reverse) {
                sortIcon = (
                    <FontIcon className={classnames('ml-icon', 'caret_up')} />
                );
            } else {
                sortIcon = (
                    <FontIcon className={classnames('ml-icon', 'caret_down')} />
                );
            }
        }
        return (
            <SortCell
                onSort={this.onSort}
                columnKey={columnKey}
                className={classnames({ [theme.sortedCell]: isSorted })}
            >
                <span title={title} className="text">
                    {title}
                </span>
                {sortIcon}
            </SortCell>
        );
    };

    onRowClick = (event, rowIndex) => {
        const { sortedSource } = this.state;
        if (this.props.onRowClick && sortedSource) {
            this.props.onRowClick(sortedSource.get(rowIndex), event);
        }
    };

    rowKeyGetter = index => {
        const { sortedSource } = this.state;
        const { rowKeyGetter } = this.props;
        if (!rowKeyGetter) {
            return undefined;
        }
        if (sortedSource && sortedSource.size) {
            return rowKeyGetter(sortedSource, index);
        }
    };

    rowClassNameGetter = index => {
        const { sortedSource } = this.state;
        const { rowClassNameGetter } = this.props;
        if (rowClassNameGetter && sortedSource && sortedSource.size) {
            return rowClassNameGetter(sortedSource, index);
        }
    };

    render() {
        const { sortedSource } = this.state;
        const {
            theme,
            model,
            onColumnResizeEndCallback,
            visible,
            className,
            emptyStateImage,
            emptyStateText,
            rowHeight,
            modelOrder
        } = this.props;
        if (!model) {
            return null;
        }

        return (
            <AutoSizer>
                {({ height, width }) => (
                    <div className={className}>
                        {(sortedSource && sortedSource.size !== 0) ||
                        !this.state.sortedSource ? (
                            <Table
                                {...this.props}
                                rowHeight={rowHeight ? rowHeight : 36}
                                rowsCount={
                                    sortedSource
                                        ? sortedSource.size
                                        : LOADING_ROW_COUNT
                                }
                                width={width}
                                height={height}
                                headerHeight={50}
                                isColumnResizing={false}
                                onColumnResizeEndCallback={
                                    onColumnResizeEndCallback
                                }
                                rowKeyGetter={this.rowKeyGetter}
                                rowClassNameGetter={this.rowClassNameGetter}
                                onRowClick={this.onRowClick}
                                className={classnames(theme.table, {
                                    [theme.tableLoading]: !this.state
                                        .sortedSource
                                })}
                            >
                                {modelOrder.map(columnKey => {
                                    if (
                                        !model[columnKey] ||
                                        !model[columnKey].visible
                                    ) {
                                        return null;
                                    }
                                    return (
                                        <Column
                                            columnKey={columnKey}
                                            key={columnKey}
                                            isResizable
                                            allowCellsRecycling
                                            header={this.getHeader}
                                            cell={this.getDataByRowAndColumn}
                                            width={model[columnKey].width}
                                            flexGrow={model[columnKey].flexGrow}
                                            fixed={model[columnKey].fixed}
                                        />
                                    );
                                })}
                            </Table>
                        ) : (
                            <div
                                className={classnames(
                                    theme.tableEmptyStateWrap,
                                    { [theme.open]: visible }
                                )}
                            >
                                <span className={theme.tableEmptyState}>
                                    <img src={emptyStateImage} />
                                    <span className={theme.text}>
                                        {emptyStateText}
                                    </span>
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </AutoSizer>
        );
    }
}

const ThemedSortedTable = themr('SortedTable', theme)(SortedTable);
export { ThemedSortedTable as SortedTable };
export default ThemedSortedTable;
